/* eslint-disable sonarjs/cognitive-complexity */

import {
  BaSeButton,
  BaSeDialogContext,
  BaSeParagraph,
  BaSeTheme,
} from '@base/react';
import * as Sebrae from '@sebrae/react-core';
import React, { createContext } from 'react';
import { useMessage } from '../../../hooks/messages/useMessage';
import { useCityService } from '../../../hooks/services/city-service';
import { useClassService } from '../../../hooks/services/class-service';
import { cpfValidator } from '../../../utils/validators/cpf-validator';

export const FormContext = createContext();

export function FormProvider({ children }) {
  const { getListClass, creteNewInterviewForLandigpage } = useClassService();
  const { listCitiesByUf } = useCityService();
  const { showModal } = React.useContext(BaSeDialogContext);
  const { tokenInfo, isAuthenticated } = Sebrae.useAuth();
  const { navigateTo } = Sebrae.useSebraeRouter();
  const { handleError } = useMessage();

  const getUrl = React.useCallback(() => {
    const url = window.location.protocol + '//' + window.location.host;
    return url;
  }, []);

  const formatCpf = React.useCallback((value) => {
    const rawValue = value.replace(/\D/g, '');
    const formattedValue = rawValue.replace(
      /(\d{3})(\d{3})(\d{3})(\d{0,2})/,
      (match, p1, p2, p3, p4) => `${p1}.${p2}.${p3}-${p4}`.replace(/-$/, ''),
    );
    return formattedValue;
  }, []);

  const handleMenuClick = React.useCallback((sectionId) => {
    const section = document.getElementById(sectionId);
    const offset = 94;
    const elementPosition =
      section.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  }, []);

  const navigateToCandidate = React.useCallback(
    (option) => {
      if (option) {
        navigateTo(`${getUrl()}/candidato`);
      }
    },
    [navigateTo, getUrl],
  );

  const openDialogErrorValidation = React.useCallback(
    (message, hasRedirect) => {
      return showModal({
        actions: [],
        size: 'small',
        title: '',
        dismissible: true,
        dismissibleIconColor: BaSeTheme.colors.defaultColors.black,
        dismissibleIconType: 'tertiary',
        content: ({ closeModal, emitEvent }) => (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 26,
              justifyContent: 'center',
            }}
          >
            <BaSeParagraph
              color={BaSeTheme.colors.institucionais.cinzaSebrae45}
            >
              <span
                color={BaSeTheme.colors.institucionais.cinzaSebrae45}
                dangerouslySetInnerHTML={{
                  __html: message,
                }}
              ></span>
            </BaSeParagraph>

            <BaSeButton
              borderRadius={'max'}
              value="Fechar"
              width="120px"
              onClick={() => {
                closeModal();
                navigateToCandidate(hasRedirect);
              }}
            />
          </div>
        ),
        onChooseAction: (action) => {},
      });
    },
    [showModal, navigateToCandidate],
  );

  const [nextScreen, setNextScreen] = React.useState(false);

  //lista de classes
  const [classes, setClasses] = React.useState([]);

  //dados pessoais
  const [personalData, setPersonalData] = React.useState({
    name: '',
    cpf: '',
    email: '',
    contato: '',
  });

  React.useEffect(() => {
    if (isAuthenticated) {
      setPersonalData((prevDto) => ({
        ...prevDto,
        name: tokenInfo.name,
        cpf: formatCpf(tokenInfo.cpf),
        email: tokenInfo.email,
      }));
    }
  }, [isAuthenticated, formatCpf, tokenInfo]);

  const handleChangePersonalData = React.useCallback((name, value) => {
    setPersonalData((prevDto) => ({
      ...prevDto,
      [name]: value,
    }));
  }, []);

  const values = Object.values(personalData);
  const hasEmptyField = values.some((val) => val === '');
  const showButtonStartInterview = !hasEmptyField;

  // PAGINAÇ˜AO
  const [pageIndex, setPageIndex] = React.useState(0);
  const [pageSize] = React.useState(10); // Tamanho fixo da página
  const [totalPages, setTotalPages] = React.useState(1);
  const [totalItems, setTotalItems] = React.useState(0);

  //seleciona o estado
  const [listUfs, setListUf] = React.useState([]);
  const [selectedUfOption, setSelectedUfOption] = React.useState(null);

  //seleciona o id da turma escolhida
  const [selectedIdClass, setSelectedIdClass] = React.useState(null);

  const getlistClassRoom = React.useCallback(
    async (dto) => {
      try {
        const response = await getListClass(dto);
        if (response.total === 0) {
          setClasses([]);
          setSelectedIdClass(null);

          return;
        }
        setTotalItems(response.total);
        setTotalPages(Math.ceil(response.total / pageSize));
        setClasses(response.items);
        setSelectedIdClass(null);
      } catch (error) {
        setClasses([]);
        setSelectedIdClass(null);
      }
    },
    [getListClass, pageSize],
  );

  //busca a cidade
  const [listCity, setListCity] = React.useState([]);
  const [selectedCityOption, setSelectedCityOption] = React.useState(null);
  const [selectCityIdForClass, setSelectCityIdForClass] = React.useState(null);

  //seleciona o id do empretec
  const [empretecId, setEmpretecId] = React.useState(null);
  const handleSelectEmpretec = React.useCallback(
    (id) => {
      const ufData = JSON.parse(selectedUfOption);
      if (ufData && ufData.label !== 'Selecione') {
        let dto = {
          pageIndex,
          pageSize,
          sortField: 'className',
          sortType: 'ASC',
          cityName: '',
          ufInitials: ufData.sigla,
          moduleId: id,
        };
        const cityData = JSON.parse(selectedCityOption);
        if (cityData && cityData.label !== 'Selecione') {
          dto.cityName = cityData.label;
        }

        getlistClassRoom(dto);
      }
      setEmpretecId(id);
    },
    [
      getlistClassRoom,
      pageIndex,
      pageSize,
      selectedCityOption,
      selectedUfOption,
    ],
  );

  const handlePageChange = React.useCallback(
    (event) => {
      setPageIndex(Number(event.target.value) - 1);
      const cityData = JSON.parse(selectedCityOption);
      const ufData = JSON.parse(selectedUfOption);

      const { label = '' } = cityData || {};
      const cityName = label === 'Selecione' ? '' : label;

      getlistClassRoom({
        pageIndex: Number(event.target.value) - 1,
        pageSize,
        sortField: 'className',
        sortType: 'ASC',
        cityName,
        ufInitials: ufData.sigla || '',
        moduleId: empretecId,
      });
    },
    [
      selectedUfOption,
      selectedCityOption,
      getlistClassRoom,
      pageSize,
      empretecId,
    ],
  );

  const handleSelectClass = React.useCallback(
    (id) => {
      setSelectedIdClass(id === selectedIdClass ? null : id);
    },
    [selectedIdClass],
  );

  const handleSelectUf = React.useCallback(
    (event) => {
      const ufData = JSON.parse(event.target.value);
      setSelectedUfOption(event.target.value);

      listCitiesByUf(ufData)
        .then((response) => {
          const newResponse = response.map((city) => {
            return {
              id: city.id,
              label: city.name,
              sigla: city.uf.initials,
            };
          });
          newResponse.unshift({
            id: 0,
            label: 'Selecione',
            sigla: 'Selecione',
          });
          setListCity(newResponse);
        })
        .catch((error) => {
          setListCity([]);
        });

      const dto = {
        pageIndex,
        pageSize,
        sortField: 'className',
        sortType: 'ASC',
        cityName: '',
        ufInitials: ufData.sigla,
        moduleId: empretecId,
      };
      getlistClassRoom(dto);
    },
    [
      setSelectedUfOption,
      listCitiesByUf,
      pageIndex,
      pageSize,
      empretecId,
      getlistClassRoom,
    ],
  );
  const handleSelectCity = React.useCallback(
    (event) => {
      const cityData = JSON.parse(event.target.value);
      const ufData = JSON.parse(selectedUfOption);

      setSelectedCityOption(event.target.value);

      const dto = {
        pageIndex,
        pageSize,
        sortField: 'className',
        sortType: 'ASC',
        cityName: cityData.label === 'Selecione' ? '' : cityData.label,
        ufInitials: ufData.sigla,
        moduleId: empretecId,
      };
      getlistClassRoom(dto);
    },
    [pageIndex, pageSize, selectedUfOption, empretecId, getlistClassRoom],
  );

  const handleCreateInterview = React.useCallback(() => {
    const ufParse = JSON.parse(selectedUfOption);
    const cityParse = JSON.parse(selectedCityOption);

    const DTO = {
      candidate: {
        cpf: personalData.cpf.replace(/[^a-zA-Z0-9]/g, ''),
        name: personalData.name,
      },
      sendEmail: true,
      city: {
        uf: {
          id: ufParse.id,
          label: ufParse.label,
          sigla: ufParse.sigla,
        },
        id: cityParse?.id ? cityParse.id : selectCityIdForClass,
      },
      email: personalData.email,
      phone: personalData.contato.replace(/[()\s-]/g, ''),
      module: {
        id: empretecId,
      },
      idClassRoom: selectedIdClass,
    };

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(DTO.email)) {
      handleError('E-mail inválido!');
      return;
    }

    if (DTO.candidate.cpf.length < 11 || !cpfValidator(DTO.candidate.cpf)) {
      handleError('CPF inválido!');
      return;
    }
    creteNewInterviewForLandigpage(DTO)
      .then((response) => {
        const hasRedirect = response?.resultValidation?.redirect;

        if (response?.resultValidation?.message) {
          openDialogErrorValidation(
            response?.resultValidation?.message,
            hasRedirect,
          );
          return;
        }

        if (hasRedirect) {
          navigateTo(`${getUrl()}/candidato`);
        }
      })
      .catch((error) => handleError('Erro ao validar os dados!'));
  }, [
    creteNewInterviewForLandigpage,
    getUrl,
    navigateTo,
    openDialogErrorValidation,
    handleError,
    empretecId,
    personalData,
    selectedCityOption,
    selectedIdClass,
    selectedUfOption,
    selectCityIdForClass,
  ]);

  return (
    <FormContext.Provider
      value={{
        classes,
        handleChangePersonalData,
        showButtonStartInterview,
        pageIndex,
        pageSize,
        setPageIndex,
        totalPages,
        totalItems,
        listUfs,
        setListUf,
        selectedUfOption,
        setSelectedUfOption,
        getlistClassRoom,
        personalData,

        handleSelectCity,
        handleSelectUf,
        handleSelectClass,
        handlePageChange,
        handleSelectEmpretec,
        listCity,
        empretecId,
        selectedIdClass,
        selectedCityOption,
        handleCreateInterview,
        formatCpf,
        nextScreen,
        setNextScreen,

        handleMenuClick,
        selectCityIdForClass,
        setSelectCityIdForClass,
      }}
    >
      {children}
    </FormContext.Provider>
  );
}
